import { toMoment, toUpper } from 'utils/normalizers';
import {
  generateCURPRules,
  generateEmailRules,
  generatePhoneRules,
  generateRFCRules,
} from 'utils/rules';
import { ciudadanoToString } from 'utils/padronesToString';
import { dataTypesEnum } from '../enums';

const fieldsInfo = {
  tipo_de_persona_alt: {
    id: 'tipo_de_persona_alt',
    nombre_en_la_bd: 'tipo_de_persona_alt',
    tipo_de_dato_del_campo: dataTypesEnum.BOOLEAN,
  },
};

const tabs = [
  {
    title: 'Datos de identificación del Contribuyente',
    fields: [
      'tipo_de_persona_alt',
      'tipo_de_persona',
      'rfc',
      'razon_social',
      'regimen_capital',
      'curp',
      'nombre',
      'apellido_paterno',
      'apellido_materno',
      'nombre_comercial',
      'regimen_fiscal',
      'ciudadano',
    ],
  },
  {
    title: 'Datos de Contacto',
    fields: [
      'lada_fijo',
      'lada_celular',
      'correo_electronico',
    ],
  },
  {
    title: 'Datos del Registro al Padrón',
    fields: [
      'unidad_de_recaudacion',
      'fecha_de_alta_de_padron_estatal',
      'fecha_inicio_operaciones_contribuyente',
      'estado_del_padron',
      'fecha_de_constitucion',
      'fecha_de_alta_shcp',
      'fecha_de_registro_patronal_imss',
      'registro_patronal_imss',
    ],
  },
];

const REPUVE = process.env.REACT_APP_ENV_NAME === 'repuve';

const configs = {
  // info general
  tipo_de_persona: {
    formItemProps: {
      label: 'Tipo de Persona (real)',
    },
    hidden: true,
    defaultValue: 1,
  },
  tipo_de_persona_alt: {
    formItemProps: {
      label: 'Tipo de Persona',
    },
    controlProps: {
      onChange: ({ formInstance, evtArgs: [val] }) => {
        const tipo_de_persona = (REPUVE ? { 0: 1, true: 2, false: 3 } : { true: 1, false: 2 })[val];
        formInstance.setFieldsValue({ tipo_de_persona });
      },
      dataSource: REPUVE ? [
        { id: 0, descripcion: 'PERSONA FÍSICA' },
        { id: true, descripcion: 'PERSONA MORAL' },
        { id: false, descripcion: 'EXTRANJERO' },
      ] : undefined,
      trueFalse: REPUVE ? undefined : ['PERSONA FÍSICA', 'PERSONA MORAL'],
    },
    defaultValue: REPUVE ? 0 : true,
    [REPUVE ? 'hideOnFalsy' : 'hideOnTruty']: ['razon_social', 'regimen_capital', 'nombre_comercial'], // fisica
    [REPUVE ? 'hideOnTruty' : 'hideOnFalsy']: ['curp', 'nombre', 'apellido_paterno', 'apellido_materno'], // moral
  },
  rfc: {
    formItemProps: {
      label: 'RFC',
      rules: generateRFCRules({
        validateUnique: true,
        tipoDePersonaName: 'tipo_de_persona',
        tipoDePadron: 15,
        required: false,
      }),
      normalize: toUpper,
    },
  },
  razon_social: {
    formItemProps: {
      label: 'Razón Social',
      required: true,
      normalize: toUpper,
    },
  },
  regimen_capital: {
    formItemProps: {
      label: 'Régimen Capital',
    },
  },
  regimen_fiscal: {
    formItemProps: {
      label: 'Régimen Fiscal (SAT)',
    },
    controlProps: {
      normalizeDataSource: (data = [], form) => {
        const tipoDePersona = form.getFieldValue('tipo_de_persona');
        return data.filter((e) => e[tipoDePersona === 1 ? 'persona_fisica' : 'persona_moral']);
      },
    },
    updateOnChange: 'tipo_de_persona_alt',
  },
  ciudadano: {
    params: [''], // prevent fill
    formItemProps: {
      label: 'Ciudadano',
      // rules: [{ required: true }],
    },
    controlProps: {
      render: (ciudadano) => ciudadanoToString(ciudadano),
      allowClear: false,
      disabled: true,
    },
  },
  curp: {
    formItemProps: {
      label: 'CURP',
      rules: generateCURPRules({ validateUnique: true, tipoDePadron: 15, required: true }),
      normalize: toUpper,
    },
  },
  nombre: {
    formItemProps: {
      label: 'Nombre',
      rules: [{ required: true }],
    },
  },
  apellido_paterno: {
    formItemProps: {
      label: 'Apellido Paterno',
      rules: [{ required: true }],
    },
  },
  apellido_materno: {
    formItemProps: {
      label: 'Apellido Materno',
    },
  },
  nombre_comercial: {
    formItemProps: {
      label: 'Nombre Comercial',
      normalize: toUpper,
      required: REPUVE,
    },
  },
  // Datos de Contacto
  lada_fijo: {
    controlProps: {
      names: {
        lada: 'lada_fijo',
        numero_de_telefono: 'telefono_fijo',
      },
      rules: {
        numero_de_celular: generatePhoneRules({ tipoDePadron: 15, validateUnique: true }),
      },
      label: 'Teléfono Fijo',
      fullSize: true,
    },
  },
  lada_celular: {
    controlProps: {
      names: {
        lada: 'lada_celular',
        numero_de_telefono: 'telefono_celular',
      },
      rules: {
        numero_de_celular: generatePhoneRules({ tipoDePadron: 15, validateUnique: true }),
      },
      label: 'Teléfono Celular',
      fullSize: true,
    },
  },
  correo_electronico: {
    formItemProps: {
      label: 'Correo electrónico',
      rules: generateEmailRules({ required: false, tipoDePadron: 15, validateUnique: true }),
    },
  },
  // Datos del Registro al Padrón
  unidad_de_recaudacion: {
    formItemProps: {
      label: 'Unidad Recaudadora',
    },
  },
  fecha_de_alta_de_padron_estatal: {
    formItemProps: {
      label: 'Fecha de Alta de Padrón Estatal',
    },
    defaultValue: toMoment(new Date()),
  },
  fecha_inicio_operaciones_contribuyente: {
    formItemProps: {
      label: 'Fecha De Inicio De Operaciones',
    },
  },
  fecha_de_constitucion: {
    formItemProps: {
      label: (form) => (form.getFieldValue('tipo_de_persona_alt')
        ? 'Fecha de nacimiento' : 'Fecha de Constitución'),
    },
    updateOnChange: 'tipo_de_persona_alt',
  },
  fecha_de_alta_shcp: {
    formItemProps: {
      label: 'Fecha de Alta SHCP/SAT',
      tooltip: 'Fecha de alta ante la Secretaria de Hacienda y crédito público y/ o SAT',
    },
  },
  fecha_de_registro_patronal_imss: {
    formItemProps: {
      label: 'Fecha de Registro Patronal',
      tooltip: 'Fecha de alta del registro patronal en el IMSS',
    },
  },
  registro_patronal_imss: {
    formItemProps: {
      label: 'Número de registro IMSS',
      normalize: toUpper,
    },
  },
  estado_del_padron: {
    formItemProps: {
      label: 'Estado del Padrón',
      required: true,
    },
    defaultValue: 2,
    disabledEnAlta: true,
  },
};

const forbiddenProps = [];

export default {
  configs,
  forbiddenProps,
  fieldsInfo,
  tabs,
};
